import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateFooter,
  EmptyStateTitle,
  Link,
} from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isEmptyString } from '@/utils/string';

export type DeliveryEmptyStateProps = {
  /** Filter-aware "There are no …" sentence, shown only when filters are active. */
  emptyMessage?: string;
  hasFilters?: boolean;
  /** Drafts view uses draft-specific copy + a different docs link. */
  isDrafted?: boolean;
  /** Person-page blank slate: suppress the generic explanatory paragraph. */
  withIndividualPersonBlankSlate?: boolean;
};

/**
 * The deliveries empty state. Ported from the Ember `<List>` empty-state block:
 * the filter-aware `emptyMessage` shows only when filters are active, then fixed
 * explanatory copy (suppressed for the person-page blank slate) and a docs link.
 */
export function DeliveryEmptyState({
  emptyMessage,
  hasFilters = false,
  isDrafted = false,
  withIndividualPersonBlankSlate = false,
}: DeliveryEmptyStateProps) {
  const taxonomy = useProductTaxonomy();
  const filterLine =
    hasFilters && !isEmptyString(emptyMessage) ? <EmptyStateDescription>{emptyMessage}</EmptyStateDescription> : null;

  if (isDrafted) {
    return (
      <EmptyState>
        <EmptyStateTitle icon="draft">Review your messages that are ready to send</EmptyStateTitle>
        {filterLine}
        <EmptyStateDescription>
          View your drafted messages to see what customers will receive. Select and send the messages that are ready.
        </EmptyStateDescription>
        <EmptyStateFooter>
          <Link href="https://docs.customer.io/queue-draft" isExternal>
            Learn more about Drafts
          </Link>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  return (
    <EmptyState>
      <EmptyStateTitle icon="delivery-log">Visualize the flow of outgoing messages</EmptyStateTitle>
      {filterLine}
      {withIndividualPersonBlankSlate ? null : (
        <EmptyStateDescription>
          {`View the delivery status for each message in the ${taxonomy.t('messageActivity.deliveryLogName')}. Filter by status to see which messages have been queued, sent, delivered, and more.`}
        </EmptyStateDescription>
      )}
      <EmptyStateFooter>
        <Link href="https://docs.customer.io/metrics" isExternal>
          {`Learn more about ${taxonomy.t('messageActivity.deliveriesLearnMoreNoun')}`}
        </Link>
      </EmptyStateFooter>
    </EmptyState>
  );
}
